import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { resolvePath } from '../services/api';
import { buildTheme } from '../theme/buildTheme';
import { SiteProvider } from '../context/SiteContext';
import EventNavbar from '../components/EventNavbar';
import ExhibitorTransition from '../components/ExhibitorTransition';
import LobbyTransition from '../components/LobbyTransition';
import { PreviewCtx } from './eventPage/contexts/index';
import { HeaderSection } from './eventPage/canvas/HeaderSection';
import EventPage from './EventPage';
import FormPage from './FormPage';
import LoginPage from './LoginPage';
import LobbyPage from './LobbyPage';
import PlenaryPage from './PlenaryPage';
import ExhibitorsPage from './ExhibitorsPage';
import ExhibitorBoothPage from './ExhibitorBoothPage';
import NotFoundPage from './NotFoundPage';

/**
 * PublicPage
 * ----------
 * Shell that stays mounted across all page navigations.
 *
 * ThemeProvider, SiteProvider, and the header are rendered ONCE at the
 * shell level and never unmount — so there's no flash on navigation.
 *
 * - Event pages: HeaderSection (from the page builder) is rendered here
 * - All other pages: EventNavbar is rendered here
 * - Not-found: no header at all
 *
 * EventPage pushes its resolved { headerSection, headerConfig, bp } up via
 * setEventHeader (in SiteContext) so the shell can render it.
 */

// ── Shimmer keyframes ─────────────────────────────────────────────────────────
const SHIMMER_STYLE = `
@keyframes gv-shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}
`;
if (typeof document !== 'undefined' && !document.getElementById('gv-shimmer-style')) {
  const s = document.createElement('style');
  s.id = 'gv-shimmer-style';
  s.textContent = SHIMMER_STYLE;
  document.head.appendChild(s);
}

const shimmerBg = (base = '#e0e0e0', shine = '#efefef') =>
  `linear-gradient(90deg, ${base} 25%, ${shine} 50%, ${base} 75%)`;

const Bone = ({ sx = {} }) => (
  <Box sx={{
    borderRadius: 1,
    background: shimmerBg(),
    backgroundSize: '600px 100%',
    animation: 'gv-shimmer 1.4s infinite linear',
    ...sx,
  }} />
);

// ── Skeletons ─────────────────────────────────────────────────────────────────

const NavbarPageSkeleton = ({ primary = '#1565C0' }) => (
  <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1, px: 2 }}>
      <Box sx={{
        width: '100%', maxWidth: 900, height: 56, borderRadius: 100,
        background: shimmerBg('#e8edf2', '#f4f7fa'),
        backgroundSize: '600px 100%',
        animation: 'gv-shimmer 1.4s infinite linear',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }} />
    </Box>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, mt: 4, mx: 'auto', width: '100%', maxWidth: 960, gap: 3, px: 3, pb: 6 }}>
      <Box sx={{ flex: '0 0 45%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Bone sx={{ height: 240, borderRadius: 3 }} />
        <Bone sx={{ height: 18, width: '70%' }} />
        <Bone sx={{ height: 14, width: '90%' }} />
        <Bone sx={{ height: 14, width: '60%' }} />
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5, pt: { xs: 0, md: 1 } }}>
        <Bone sx={{ height: 22, width: '45%' }} />
        <Bone sx={{ height: 14, width: '80%' }} />
        <Bone sx={{ height: 44, borderRadius: 1.5 }} />
        <Bone sx={{ height: 44, borderRadius: 1.5 }} />
        <Bone sx={{ height: 44, borderRadius: 1.5 }} />
        {/* <Bone sx={{ height: 48, borderRadius: 100, mt: 1, background: shimmerBg(`${primary}55`, `${primary}33`) }} /> */}
      </Box>
    </Box>
  </Box>
);

const EventPageSkeleton = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <Bone sx={{ height: 72, borderRadius: 0 }} />
    <Bone sx={{ height: { xs: 260, md: 420 }, borderRadius: 0 }} />
    <Box sx={{ px: { xs: 2, md: 8 }, py: 5, display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Bone sx={{ flex: 1, height: 140, borderRadius: 2 }} />
        <Bone sx={{ flex: 1, height: 140, borderRadius: 2 }} />
        <Bone sx={{ flex: 1, height: 140, borderRadius: 2 }} />
      </Box>
      <Bone sx={{ height: 20, width: '40%', mx: 'auto' }} />
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Bone sx={{ flex: 1, height: 100, borderRadius: 2 }} />
        <Bone sx={{ flex: 1, height: 100, borderRadius: 2 }} />
      </Box>
    </Box>
  </Box>
);

const LobbyPageSkeleton = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1, px: 2 }}>
      <Box sx={{
        width: '100%', maxWidth: 900, height: 56, borderRadius: 100,
        background: shimmerBg('#e8edf2', '#f4f7fa'),
        backgroundSize: '600px 100%',
        animation: 'gv-shimmer 1.4s infinite linear',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }} />
    </Box>
    <Bone sx={{ flex: 1, minHeight: 500, borderRadius: 2, mx: 2, mb: 2 }} />
  </Box>
);

const PageSkeleton = ({ pageType, primary }) => {
  if (pageType === 'event') return <EventPageSkeleton />;
  if (pageType === 'lobby' || pageType === 'plenary' || pageType === 'exhibitors') return <LobbyPageSkeleton />;
  return <NavbarPageSkeleton primary={primary} />;
};

// ── Top progress bar ──────────────────────────────────────────────────────────
const TopProgressBar = ({ loading, color }) => {
  const [width,   setWidth]   = useState(0);
  const [opacity, setOpacity] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (loading) {
      setWidth(0);
      setOpacity(1);
      setVisible(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setWidth(75));
      });
      timerRef.current = setTimeout(() => setWidth(90), 800);
      return () => { cancelAnimationFrame(raf); clearTimeout(timerRef.current); };
    } else {
      clearTimeout(timerRef.current);
      setWidth(100);
      timerRef.current = setTimeout(() => {
        setOpacity(0);
        timerRef.current = setTimeout(() => { setVisible(false); setWidth(0); }, 400);
      }, 200);
      return () => clearTimeout(timerRef.current);
    }
  }, [loading]);

  if (!visible) return null;

  return (
    <Box sx={{
      position: 'fixed', top: 0, left: 0, right: 0,
      height: 3, zIndex: 2000,
      opacity,
      transition: 'opacity 0.4s ease',
      pointerEvents: 'none',
    }}>
      <Box sx={{
        height: '100%',
        width: `${width}%`,
        bgcolor: color,
        transition: width === 100
          ? 'width 0.2s ease'
          : width === 0
          ? 'none'
          : 'width 0.6s cubic-bezier(0.1,0.5,0.5,1)',
        boxShadow: `0 0 8px ${color}88`,
      }} />
    </Box>
  );
};

// ── Responsive breakpoint hook (mirrors EventPage) ────────────────────────────
function useBreakpoint() {
  const getbp = () => window.innerWidth <= 480 ? 'mobile' : window.innerWidth <= 900 ? 'tablet' : 'desktop';
  const [bp, setBp] = useState(getbp);
  useEffect(() => {
    const h = () => setBp(getbp());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return bp;
}

// ═════════════════════════════════════════════════════════════════════════════
const PublicPage = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const path = location.pathname.replace(/^\//, '');
  const bp = useBreakpoint();

  // ── Resolution state ───────────────────────────────────────────────────────
  const [result,         setResult]         = useState(null);
  const [contentLoading, setContentLoading] = useState(true);
  const [error,          setError]          = useState(null);

  // ── Event page header state ────────────────────────────────────────────────
  // EventPage calls setEventHeader({ headerSection, headerConfig, bp }) so the
  // shell can render the builder header instead of EventNavbar.
  const [eventHeader, setEventHeader] = useState(null);

  // Cache the last resolved page type so skeleton + header stay stable
  // during transitions (no flash between states).
  const lastEventRef      = useRef(null);
  const lastResultTypeRef = useRef(null);

  // Prefetch cache
  const prefetchCache = useRef({});
  const prefetchRef   = useRef(null);
  prefetchRef.current = (targetPath) => {
    const key = targetPath.replace(/^\//, '');
    if (prefetchCache.current[key]) return;
    resolvePath(key)
      .then(res => { prefetchCache.current[key] = res.data; })
      .catch(() => {});
  };

  // ── Page-ready signaling ──────────────────────────────────────────────────
  const [pageReady,         setPageReady]         = useState(false);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const pageReadyRef   = useRef(null);
  pageReadyRef.current = () => {
    setPageReady(true);
    setTransitionLoading(false);
  };

  const resetPageReady = () => {
    setPageReady(false);
    setTransitionLoading(true);
  };

  const [activeLobbyTransition, setActiveLobbyTransition] = useState(null);
  const lobbyTransitionNavigatedRef = useRef(false);
  const afterTransitionRef = useRef(null);

  const startLobbyTransitionRef = useRef(null);
  startLobbyTransitionRef.current = ({ videoUrl, path: targetPath }) => {
    lobbyTransitionNavigatedRef.current = false;
    resetPageReady();
    prefetchRef.current?.(targetPath);
    setActiveLobbyTransition({ videoUrl, path: targetPath });
  };

  const [activeTransition, setActiveTransition] = useState(null);
  const transitionNavigatedRef = useRef(false);

  const startTransitionRef = useRef(null);
  startTransitionRef.current = ({ srcRect, thumbnailUrl, videoUrl, path: targetPath }) => {
    transitionNavigatedRef.current = false;
    resetPageReady();
    prefetchRef.current?.(targetPath);
    setActiveTransition({ srcRect, thumbnailUrl, videoUrl, path: targetPath });
  };

  useEffect(() => {
    let cancelled = false;
    setError(null);

    const cached = prefetchCache.current[path];
    if (cached) {
      delete prefetchCache.current[path];
      setResult(cached);
      lastResultTypeRef.current = cached.type;
      setContentLoading(false);
      return;
    }

    setContentLoading(true);
    resolvePath(path)
      .then(res => {
        if (cancelled) return;
        setResult(res.data);
        lastResultTypeRef.current = res.data.type;
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Page not found.');
      })
      .finally(() => {
        if (!cancelled) setContentLoading(false);
      });

    return () => { cancelled = true; };
  }, [path]);

  // ── Derive stable shell values ────────────────────────────────────────────
  const event = result
    ? (result.type === 'event'      ? result.data
    :  result.type === 'login'      ? result.data
    :  result.type === 'lobby'      ? result.event
    :  result.type === 'plenary'    ? result.event
    :  result.type === 'exhibitors' ? result.event
    :  result.type === 'exhibitor'  ? result.event
    :  result.event || null)
    : lastEventRef.current;

  if (event && event !== lastEventRef.current) {
    lastEventRef.current = event;
  }

  const branding = event?.branding || {};
  const primary  = branding.primaryColor || '#1565C0';
  const muiTheme = buildTheme(branding);

  const navMode = !result ? 'event'
    : result.type === 'lobby'      ? 'lobby'
    : result.type === 'plenary'    ? 'plenary'
    : result.type === 'exhibitors' ? 'plenary'
    : result.type === 'exhibitor'  ? 'plenary'
    : 'event';

  const siteValue = {
    event,
    forms:                  result?.forms || [],
    organization:           result?.organization || null,
    lobby:                  result && ['lobby'].includes(result.type) ? result.data : null,
    navMode,
    prefetchRef,
    startTransitionRef,
    startLobbyTransitionRef,
    pageReadyRef,
    afterTransitionRef,
    isTransitioning: !!(activeLobbyTransition || activeTransition),
    setEventHeader,
    // Exposed so FormPage / LoginPage can compute their top-padding offset
    shellHeaderConfig: event?.layout?.header || {},
    // Exposed so FormPage / LoginPage can render the builder footer
    shellFooterConfig: event?.layout?.footer || {},
  };

  // ── Handle unauthorized ───────────────────────────────────────────────────
  if (result?.type === 'unauthorized') {
    const loginPath = result.urlMode === 'flat'
      ? '/login'
      : `/${result.eventSlug}/login`;
    window.location.replace(loginPath);
    return null;
  }

  // Use the settled result type for header decisions — never the cached last type.
  // lastResultTypeRef is only used for skeleton shape selection, not header switching.
  // During loading: keep whatever header was already mounted (no switch, no flash).
  const settledType  = result?.type;                // null while loading
  const resolvedType = settledType || lastResultTypeRef.current; // for skeleton only
  const isNotFound   = !contentLoading && (error || !result || result.type === 'not_found');

  // The LobbyEngine is completely self-contained (full-screen, fixed position,
  // its own header, loading screen, transitions). The shell must stay out of
  // its way — no navbar, no progress bar, no opacity wrapper.
  const isLobbyEngine = settledType === 'lobby';

  // Pages that show the builder HeaderSection instead of EventNavbar.
  // - 'event'  → pushed up from EventPage via setEventHeader (may include per-page override)
  // - 'form'   → synthesised here from event.layout.header
  // - 'login'  → synthesised here from event.layout.header
  // While loading (settledType null), preserve the previous header — don't flip.
  const BUILDER_HEADER_TYPES = new Set(['event', 'form', 'login']);
  const isBuilderHeaderPage  = BUILDER_HEADER_TYPES.has(settledType);
  const showNavbar = !isNotFound && !isBuilderHeaderPage && !isLobbyEngine && (settledType !== null || !BUILDER_HEADER_TYPES.has(lastResultTypeRef.current));

  // For form/login pages the event is already available in the shell via `event`.
  // Derive header props directly from event.layout.header so we don't need those
  // pages to call setEventHeader themselves.
  const eventLayout = event?.layout || {};
  const shellHeaderConfig = eventLayout.header || {};
  // Stub section — HeaderSection only needs section.id for click-to-select (disabled in preview)
  const SHELL_HEADER_SECTION = { id: 'shell-header', type: 'header' };

  // Resolve which header source to use
  const activeHeaderSection = isBuilderHeaderPage
    ? (settledType === 'event'
        ? (eventHeader?.headerSection ?? null)          // EventPage pushes the real section
        : (Object.keys(shellHeaderConfig).length ? SHELL_HEADER_SECTION : null))  // form/login
    : null;
  const activeHeaderConfig = isBuilderHeaderPage
    ? (settledType === 'event'
        ? (eventHeader?.headerConfig ?? shellHeaderConfig)
        : shellHeaderConfig)
    : null;
  const activeHeaderBp = settledType === 'event' ? (eventHeader?.bp ?? bp) : bp;

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <SiteProvider value={siteValue}>

        {/* Engine manages its own progress bar — suppress shell bar for lobby */}
        {!isLobbyEngine && (
          <TopProgressBar loading={contentLoading || transitionLoading} color={primary} />
        )}

        {/* ── Header slot ────────────────────────────────────────────────────
            - Not-found:                 no header
            - Settled as event:          builder HeaderSection (pushed from EventPage)
            - Settled as form / login:   builder HeaderSection (from event.layout.header)
            - Settled as lobby:          no header (engine renders its own EngineHeader)
            - Settled as lobby/plenary…: global EventNavbar
            - Still loading:             keep previous header (no flash)
        */}
        {!isNotFound && !isLobbyEngine && (
          isBuilderHeaderPage
            ? (activeHeaderSection
                ? (
                  <PreviewCtx.Provider value={true}>
                    <HeaderSection
                      section={activeHeaderSection}
                      config={activeHeaderConfig}
                      selected={false}
                      onSelectHeader={NOOP}
                      breakpoint={activeHeaderBp}
                    />
                  </PreviewCtx.Provider>
                )
                : null
              )
            : null
        )}

        {/* Page content */}
        <PageContent
          result={result}
          loading={contentLoading}
          error={error}
          resolvedType={resolvedType}
          primary={primary}
          isLobbyEngine={isLobbyEngine}
        />

        {activeLobbyTransition && (
          <LobbyTransition
            videoUrl={activeLobbyTransition.videoUrl}
            pageReady={pageReady}
            onReadyToNavigate={() => {
              if (!lobbyTransitionNavigatedRef.current) {
                lobbyTransitionNavigatedRef.current = true;
                navigate(activeLobbyTransition.path);
              }
            }}
            onComplete={() => {
              setActiveLobbyTransition(null);
              const cb = afterTransitionRef.current;
              afterTransitionRef.current = null;
              cb?.();
            }}
          />
        )}

        {activeTransition && (
          <ExhibitorTransition
            srcRect={activeTransition.srcRect}
            thumbnailUrl={activeTransition.thumbnailUrl}
            videoUrl={activeTransition.videoUrl}
            pageReady={pageReady}
            onReadyToNavigate={() => {
              if (!transitionNavigatedRef.current) {
                transitionNavigatedRef.current = true;
                navigate(activeTransition.path);
              }
            }}
            onComplete={() => setActiveTransition(null)}
          />
        )}

      </SiteProvider>
    </ThemeProvider>
  );
};

// ── NOOP for HeaderSection in preview mode ────────────────────────────────────
const NOOP = () => {};

// ── Page content — skeleton while loading, fade in when ready ────────────────
const PageContent = ({ result, loading, error, resolvedType, primary, isLobbyEngine }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!loading) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setVisible(false);
    }
  }, [loading]);

  const isNotFound = !loading && (error || !result || result.type === 'not_found');

  if (loading && !result) {
    // Show a dark full-screen placeholder while the lobby resolver is running
    // (the engine will immediately take over with its own loading screen)
    if (resolvedType === 'lobby') {
      return <Box sx={{ width: '100vw', height: '100vh', background: '#0a0e1a' }} />;
    }
    return <PageSkeleton pageType={resolvedType} primary={primary} />;
  }

  if (isNotFound) {
    return <NotFoundPage />;
  }

  if (result.type === 'form_closed') {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', p: 3, gap: 2,
        opacity: visible ? 1 : 0, transition: 'opacity 0.35s ease',
      }}>
        <Alert severity="warning" sx={{ maxWidth: 480 }}>{result.message}</Alert>
        <Button variant="outlined" onClick={() => window.history.back()}>Go Back</Button>
      </Box>
    );
  }

  // The LobbyEngine is position:fixed and full-screen — render it bare with
  // no opacity wrapper, no minHeight, no fade animation.
  if (isLobbyEngine) {
    return (
      <>
        {result.type === 'lobby' && <LobbyPage preloadedLobby={result.data} requireLogin={result.requiresAuth !== false} />}
      </>
    );
  }

  return (
    <Box sx={{
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.35s ease',
      minHeight: '100vh',
    }}>
      {result.type === 'event'      && <EventPage preloadedEvent={result.data} preloadedOrganization={result.organization} preloadedForms={result.forms} preloadedPageSlug={result.pageSlug || null} />}
      {result.type === 'form'       && <FormPage  preloadedForm={result.data}  preloadedOrganization={result.organization} />}
      {result.type === 'login'      && <LoginPage />}
      {result.type === 'plenary'    && <PlenaryPage preloadedPlenary={result.data} preloadedLobby={null} />}
      {result.type === 'exhibitors' && <ExhibitorsPage preloadedExhibitors={result.data} />}
      {result.type === 'exhibitor'  && <ExhibitorBoothPage preloadedExhibitor={result.data} />}
    </Box>
  );
};

export default PublicPage;